import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { orderSamplesService, saveFile } from '@/services/order-samples.service';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { queryClient } from '@/lib/query-client';
import type { OrderSampleJob, OrderSampleStore } from '@/types';

export const ORDER_SAMPLE_POLL_MS = 3000;

export const isBuilding = (job: OrderSampleJob | null | undefined) =>
  job?.status === 'queued' || job?.status === 'running';

const storesQueryAtom = atomWithQuery((get) => {
  const id = get(selectedAccountIdAtom);
  return {
    queryKey: ['order-sample-stores', id],
    enabled: id != null,
    queryFn: () => orderSamplesService.stores(id as number)
  };
});

/** Each brand's Shopify store of the selected account, with its latest test order sheet. */
export const useOrderSampleStores = () => {
  const [result] = useAtom(storesQueryAtom);
  return result;
};

/**
 * One store's test order sheet: start it, follow it while machine builds it (about half a
 * minute), download it when it's done.
 *
 * A sheet still building when the tab opens, started by anyone, is picked up and followed
 * too. The stores list is refreshed when a sheet settles, so its "latest sheet" stays true.
 */
export const useOrderSample = (store: OrderSampleStore | null) => {
  const accountId = useAtomValue(selectedAccountIdAtom);
  const [job, setJob] = useState<OrderSampleJob | null>(store?.latestJob ?? null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const refreshStores = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['order-sample-stores', accountId] }),
    [accountId]
  );

  const poll = useCallback(
    (jobId: string) => {
      if (accountId == null) return;
      timer.current = setTimeout(async () => {
        try {
          const next = await orderSamplesService.job(accountId, jobId);
          setJob(next);
          if (isBuilding(next)) poll(jobId);
          else await refreshStores();
        } catch (error) {
          // A missed poll is not a failed sheet: machine keeps building it.
          console.warn('useOrderSample: poll failed, retrying', { jobId, error });
          poll(jobId);
        }
      }, ORDER_SAMPLE_POLL_MS);
    },
    [accountId, refreshStores]
  );

  const latestJob = store?.latestJob ?? null;
  useEffect(() => {
    stop();
    setJob(latestJob);
    if (latestJob && isBuilding(latestJob)) poll(latestJob.jobId);
    return stop;
    // Re-run only when the store or its latest sheet changes, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.storeId, latestJob?.jobId, latestJob?.status, poll]);

  const start = async () => {
    if (accountId == null || !store) throw new Error('No store selected');
    stop();
    const started = await orderSamplesService.start(accountId, store.storeId);
    setJob(started);
    poll(started.jobId);
    return started;
  };

  const download = async () => {
    if (accountId == null || !job) throw new Error('No sheet to download');
    const { blob, fileName } = await orderSamplesService.download(accountId, job);
    saveFile(blob, fileName);
  };

  return { job, start, download };
};
